import { generateClient } from "@aws-amplify/api";
import { useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { useEffect } from "react";
import { doctorKeys } from "~/api/queries/doctors";
import { useCurrentUser } from "~/auth/UserContext";
import { fetchAllMedicalAssistants } from "~/services/assistant";
import { doctorService } from "~/services/doctor";
import { fetchAllLocations } from "~/services/location";
import { fetchAllOnsiteOpsConfigs } from "~/services/onsiteOpsConfig";
import {
  allAssistantsAtom,
  allDoctorsAtom,
  allLocationsAtom,
  allOnsiteOpsConfigsAtom,
  referenceDataLoadedAtom,
} from "~/state/atoms";
import { AuthUserGroup } from "~/types/auth";

const client = generateClient({ authMode: "userPool" });

export const useInitializeData = (userGroup: AuthUserGroup | null) => {
  const queryClient = useQueryClient();
  const { isDemo, loading: userLoading } = useCurrentUser();
  const setAllAssistants = useSetAtom(allAssistantsAtom);
  const setAllDoctors = useSetAtom(allDoctorsAtom);
  const setAllLocations = useSetAtom(allLocationsAtom);
  const setAllOnsiteOpsConfigs = useSetAtom(allOnsiteOpsConfigsAtom);
  const setReferenceDataLoaded = useSetAtom(referenceDataLoadedAtom);

  useEffect(() => {
    void (async () => {
      try {
        // Wait for user data to load to determine isDemo status
        if (!userGroup || userLoading) {
          return;
        }

        const [assistants, doctors, locations] = await Promise.all([
          fetchAllMedicalAssistants(client, isDemo),
          doctorService.fetchAllDoctors(userGroup, undefined, isDemo),
          fetchAllLocations(client),
        ]);

        setAllAssistants(assistants);
        setAllDoctors(doctors);
        setAllLocations(locations);
        // Flip before the onsite-ops fetch below so ID-valued fields stop
        // showing their loading state as soon as the lists they read are in.
        setReferenceDataLoaded(true);

        // Set React Query cache for doctors under the isDemo-scoped key so
        // useDoctors() picks up the seeded list without re-fetching.
        queryClient.setQueryData(doctorKeys.lists(undefined, isDemo), doctors);
        // Also set individual doctor caches
        doctors.forEach((doctor) => {
          queryClient.setQueryData(doctorKeys.detail(doctor.id), doctor);
        });

        // Fetch onsite ops configs separately to avoid blocking other data
        try {
          const onsiteOpsConfigs = await fetchAllOnsiteOpsConfigs(client);
          setAllOnsiteOpsConfigs(onsiteOpsConfigs);
          console.log(
            `✅ Loaded ${onsiteOpsConfigs.length} onsite ops configs`,
          );
        } catch (configError) {
          console.error("Failed to fetch onsite ops configs:", configError);
          setAllOnsiteOpsConfigs([]);
        }
      } catch (error) {
        console.error("Failed to fetch data", error);
        // Optionally set fallback values or error states
        setAllAssistants([]);
        setAllDoctors([]);
        setAllLocations([]);
        setAllOnsiteOpsConfigs([]);
        // Settled, just empty — consumers must fall back to showing the raw
        // value rather than spinning forever.
        setReferenceDataLoaded(true);
      }
    })();
  }, [
    queryClient,
    setAllAssistants,
    setAllDoctors,
    setAllLocations,
    setAllOnsiteOpsConfigs,
    setReferenceDataLoaded,
    userGroup,
    isDemo,
    userLoading,
  ]);
};
